import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import { withRouter } from 'react-router-dom';
import { AppToaster } from 'components/AppToaster';
import { Intent } from '@blueprintjs/core';
import { Container } from 'components/styled-components';
import * as Yup from 'yup';
import * as actions from 'actions/vendor-request';
import { fetchPrescreenQuestions } from 'actions/prescreen';
import { fetchAllDepartments, fetchAllRoles } from 'actions/utils';
import { fetchUsers } from 'api/users';
import { submitVendorRequest } from 'api/vendor-request';
import { uploadFile, fetchQuestionStatus } from 'api/utils';
import VendorRequestForm from 'components/forms/vendor-request/vendor-request';
import VendorPrescreenForm from 'components/forms/vendor-request/vendor-prescreen';
import VendorRequestHeader from 'components/forms/vendor-request/header';
import VendorRequestNavigation from 'components/forms/vendor-request/navigation';
import map from 'lodash/map';
import join from 'lodash/join';
import { fileTypeFilter } from 'utils/functions';
import Loading from 'components/loading';

const ValidationSchema = Yup.object().shape({
  sponsor: Yup.object()
    .shape({
      userID: Yup.number(),
      email: Yup.string()
        .email('Invalid email')
        .required('Required'),
    })
    .required('Must have a sponsor'),
  departmentsRequesting: Yup.array()
    .min(1, 'Select at least one requesting department')
    .required(),
  vendorInformation: Yup.object()
    .required('Must have a Third Party')
    .nullable(),
  productServiceName: Yup.object()
    .required('Must have a Product/Service')
    .nullable(),
  vendorContact: Yup.array().of(Yup.object().shape({
    firstName: Yup.string()
    //   .min(2, 'Too Short!')
      .max(200, 'Too Long!')
      .required('Required')
      .nullable(),
    lastName: Yup.string()
    //   .min(2, 'Too Short!')
      .max(200, 'Too Long!')
      .required('Required')
      .nullable(),
    email: Yup.string()
      .max(200, 'Too Long!')
      .email('Invalid email')
      .required('Required')
      .nullable(),
  }))
    .nullable()
    .required('Must have a Third Party contact'),
	description: Yup.string()
		.min(10, 'The description needs to be longer.')
		.required('Required')
    .nullable(),
});

class VendorRequest extends Component {
  state = {
    currentStep: 1,
    totalSteps: 2,
    attachments: [],
    showSponsors: false,
    sponsors: [],
    appUser: this.props.appUser,
    uploadedFiles: [],
    failedFiles: [],
  };

  componentDidMount() {
    this.props.fetchAllVendors();
    this.props.fetchAllDepartments();
    this.props.fetchAllRoles().then(() => this.fetchSponsors());
    fetchQuestionStatus()
      .then(({ data }) => {
        const activeID = data.find(status => status.statusName === 'Active');
        this.props.fetchPrescreenQuestions({
          start: 0,
          limit: -1,
          status: parseInt(activeID.versionStatusID),
        });
      })
      .catch(err => console.error(err));
  }
  componentWillUnmount() {
    this.props.clearVendorProducts();
    this.props.clearVendorContacts();
  }

  fetchSponsors = () => {
    fetchUsers({ start: 0, limit: -1 })
      .then(res =>
        this.setState({
          sponsors: res.data.result.filter(user => user.role !== 'THIRD_PARTY'),
        })
      )
      .catch(err => console.error(err));
  };

  handleGoBack = validateForm => {
    this.setState({ currentStep: this.state.currentStep - 1 });
    validateForm();
  };

  nextPage = () => {
    this.setState({ currentStep: this.state.currentStep + 1 });
  };

  handleOnDrop = acceptedFiles => {
    acceptedFiles = acceptedFiles.filter(file => fileTypeFilter(file));
    if (acceptedFiles.length === 0) {
      AppToaster.show({
        message:
          'The files could not be uploaded due to their file type. Try uploading a PDF, MSG, Excel / Word / PowerPoint, or html file',
        intent: Intent.DANGER,
      });
      return;
    }

    this.setState({
      attachments: [...this.state.attachments, ...acceptedFiles],
    });
  };

  handleAttachmentRemove = attachment => {
    const data = this.state.attachments.filter(i => i !== attachment);
    this.setState({ attachments: data });
  };

  /**
   * to show and hide sponsor drop down
   */
  handleChangeSponsorClick = showSponsors => this.setState({ showSponsors });

  submit = (values, actions) => {
    const prescreenCheckPassed = values.prescreen.every(p => {
      if (p) {
        if (p.length >= 1 || p.answerID) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
    if (
      !prescreenCheckPassed ||
      values.prescreen.length !== this.props.prescreen.totalCount
    ) {
      actions.setErrors({
        prescreen: 'Each question must have at least one answer',
      });
      actions.setSubmitting(false);
    } else {
      submitVendorRequest(values)
        .then(res => {
          actions.setSubmitting(false);

          const promisedAttachments = map(
            this.state.attachments,
            attachment => {
              return new Promise((resolve, reject) => {
                uploadFile('PRESCREEN', res.data.requestID, attachment)
                  .then(res => {
                    return this.setState(
                      {
                        uploadedFiles: [
                          ...this.state.uploadedFiles,
                          attachment.name,
                        ],
                      },
                      () => resolve(res)
                    );
                  })
                  .catch(error => {
                    return this.setState(
                      {
                        failedFiles: [
                          ...this.state.failedFiles,
                          attachment.name,
                        ],
                      },
                      () => reject(error)
                    );
                  });
              });
            }
          );
          Promise.all(promisedAttachments).then(() => {
            if (this.state.uploadedFiles.length > 0) {
              AppToaster.show({
                message: `Successfully uploaded: ${join(
                  this.state.uploadedFiles,
                  ', '
                )}`,
                intent: Intent.SUCCESS,
              });
            }
            if (this.state.failedFiles.length > 0) {
              AppToaster.show({
                message: `Failed to uploaded: ${join(
                  this.state.failedFiles,
                  ', '
                )}`,
                intent: Intent.DANGER,
              });
            }
          });
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.history.push('/');
        })
        .catch(error => {
          actions.setSubmitting(false);
          AppToaster.show({
            message: error.response.data.message,
            intent: Intent.DANGER,
          });
        });
    }
  };

  render() {
    const initialPrescreen = new Array(this.props.prescreen.totalCount).fill(
      false
    );
    if (this.props.isLoadedPrescreen) {
      return (
        <Container>
          <Formik
            initialValues={{
              sponsor: this.state.appUser,
              departmentsRequesting: [],
              vendorInformation: '',
              productServiceName: '',
              vendorContact: [],
              description: '',
              reqPoInvoice: '',
              attachments: [],
              prescreen: initialPrescreen,
            }}
            onSubmit={this.submit}
            validationSchema={ValidationSchema}
            validateOnBlur={true}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              setTouched,
              values,
              errors,
              touched,
              isSubmitting,
              dirty,
              validateForm,
              isValid,
              setErrors,
            }) => (
              <Form>
                <VendorRequestHeader
                  currentStep={this.state.currentStep}
                  totalSteps={this.state.totalSteps}
                />
                {this.state.currentStep === 1 ? (
                  <VendorRequestForm
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    setFieldTouched={setFieldTouched}
                    handleOnDrop={this.handleOnDrop}
                    handleAttachmentRemove={this.handleAttachmentRemove}
                    showSponsors={this.state.showSponsors}
                    sponsors={this.state.sponsors}
                    setSponsor={this.setSponsor}
                    handleChangeSponsorClick={this.handleChangeSponsorClick}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    attachments={this.state.attachments}
                    appUser={this.state.appUser}
                    isValid={isValid}
                    {...this.props}
                  />
                ) : (
                  <VendorPrescreenForm
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    {...this.props}
                  />
                )}
                <VendorRequestNavigation
                  currentStep={this.state.currentStep}
                  totalSteps={this.state.totalSteps}
                  nextPage={this.nextPage}
                  handleGoBack={this.handleGoBack}
                  handleSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  errors={errors}
                  setErrors={setErrors}
                  dirty={dirty}
                  isValid={isValid}
                  validateForm={validateForm}
                  setTouched={setTouched}
                />
              </Form>
            )}
          </Formik>
        </Container>
      );
    } else {
      return <Loading size={200} />;
    }
  }
}

const mapStateToProps = state => ({
  vendors: state.vendorRequest.vendors,
  departments: state.utils.departments,
  roles: state.utils.roles,
  contacts: state.vendorRequest.contacts,
  appUser: state.auth.appUser,
  products: state.vendorRequest.products,
  isFetching: state.vendorRequest.isFetching,
  prescreen: state.prescreen.questions,
  isLoadedPrescreen: state.prescreen.questionsLoaded,
});

VendorRequest = withRouter(
  connect(
    mapStateToProps,
    { fetchAllDepartments, fetchAllRoles, fetchPrescreenQuestions, ...actions }
  )(VendorRequest)
);

export default VendorRequest;
